import React, { useRef } from "react";
import { Button } from "@material-ui/core";
import "./UploadUsersFile.scss";
import { MposWebPortalApi } from "../../../services/api/MposWebPortalApi";
import ConfirmDialog from "../../dialog/ConfirmDialog";
import { useHistory } from "react-router-dom";
import { MposLoader } from "../../loader/MposLoader";
import {
  errorMapping,
  IUploadUsersVerifyResponse,
  IValidationError,
} from "../../../models/UploadUsersVerifyResponse";
import InfoDialog from "../../dialog/InfoDialog";
import FileSaver from "file-saver";

const formatErrorMessage = (error: IValidationError): string => {
  const baseMessage = errorMapping[error.type];

  switch (error.type) {
    case "MISSING_EMAIL":
    case "MISSING_TID":
    case "INVALID_FILE_FORMAT":
      return `Errore sulla riga ${error.row}: ${baseMessage}`;
    case "INVALID_TID":
      return `Errore: il TID «${error.value}» ${baseMessage}`;
    case "INVALID_TYPE_TID":
      return `Errore: il TID «${error.value}» non corrisponde alla tipologia «${error.field}».`;
    case "USERNAME_NOT_EXISTS":
      return `Errore: ${baseMessage}`;
    case "USERNAME_EMAIL_MISMATCH":
      return `Errore: username "${error.value}" ${baseMessage} "${error.additionalValue}"`;
    case "ROLE_TOO_LONG":
      return `Errore: la lunghezza del valore "${error.value}" ${baseMessage}`;
    case "INVALID_EMAIL_FORMAT":
      return `Errore: l'indirizzo email "${error.value}" ${baseMessage}`;
    default:
      return `Errore: ${baseMessage}`;
  }
};

const UploadUsersFile: React.FC<any> = ({ children, onUploadSuccess }) => {
  const [uploadFile, setUploadFile] = React.useState<File | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = React.useState(false);
  const [loading, setIsLoading] = React.useState(false);
  const [
    { associationsBeforeUpload, associationsAfterUpload },
    setUploadUsersVerifyResponse,
  ] = React.useState<IUploadUsersVerifyResponse>({
    associationsBeforeUpload: 0,
    associationsAfterUpload: 0,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const history = useHistory();

  const redirectToErrorPage = (
    title: string = "Si è verificato un errore durante l'upload del file",
    description: string = "Riprova più tardi. Grazie per la pazienza"
  ) => {
    history.push("/result", {
      isSuccess: false,
      errorTitle: title,
      errorMessage: description,
      button: "TORNA ALLE UTENZE",
      data: { from: "Users", operation: "uploadUsers" },
    });
  };

  const handleButtonClick = (): void => {
    fileInputRef.current?.click();
  };

  const handleValidationFile = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsLoading(true);

    // Reset input value so selecting the same file again triggers onChange
    if (event.target) {
      event.target.value = "";
    }

    // Crea FormData per multipart upload
    const formData = new FormData();
    formData.append("file", file);

    MposWebPortalApi.getInstance()
      .uploadUsersVerify(formData)
      .subscribe(
        (response: IUploadUsersVerifyResponse) => {
          // Se ci sono errori di validazione mostra la pagina di errore,
          if (
            response.validationErrors &&
            response.validationErrors.length > 0
          ) {
            const firstError = response.validationErrors[0];
            let errorMessage = formatErrorMessage(firstError);
            let errorTitle = "Errore nella validazione del file";
            redirectToErrorPage(errorTitle, errorMessage);
            return;
          }
          // procede al dialog di conferma
          setUploadFile(file);
          setUploadUsersVerifyResponse(response);
          setShowConfirmDialog(true);
          setIsLoading(false);
        },
        (error) => {
          setIsLoading(false);
          // Gestisci errori di validazione dal backend (HTTP 500 con validationErrors)
          let errorTitle = "",
            errorMessage = "";
          if (
            error.data?.validationErrors &&
            error.data?.validationErrors?.length > 0
          ) {
            const firstError = error.data.validationErrors[0];
            errorMessage = formatErrorMessage(firstError);
            errorTitle = "Errore nella validazione del file";
          }
          redirectToErrorPage(errorTitle, errorMessage);
        }
      );
  };

  const handleConfirmUpload = () => {
    if (uploadFile === null) return;

    setIsLoading(true);

    // Crea FormData per multipart upload
    const formData = new FormData();
    formData.append("file", uploadFile);

    MposWebPortalApi.getInstance()
      .uploadUsers(formData)
      .subscribe(
        ({ blob, fileName }) => {
          if (blob) {
            // scarica il file con le modifiche apportate
            FileSaver.saveAs(blob, fileName || "utenze.xlsx");
          }
          setIsLoading(false);
          // chiude dialog di conferma
          setShowConfirmDialog(false);
          // chiama la callback di successo che richiama l'endpoint /getUsers per avere la lista aggiornata degli utenti
          onUploadSuccess?.();
          // apre dialog di successo
          setShowSuccessDialog(true);
        },
        (error) => {
          setIsLoading(false);
          // chiude il dialog di conferma
          setShowConfirmDialog(false);
          redirectToErrorPage();
        }
      );
  };

  const handleCancelUpload = () => {
    setShowConfirmDialog(false);
    setUploadFile(null);
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleValidationFile}
        accept=".xlsx,.xls"
        style={{ display: "none" }}
        disabled={loading}
      />
      <Button onClick={handleButtonClick} id="import-users" disabled={loading}>
        {children}
      </Button>
      <ConfirmDialog
        id="confirm-dialog"
        show={showConfirmDialog}
        onClose={handleCancelUpload}
        onConfirm={handleConfirmUpload}
        title="Confermi di voler procedere alle seguenti modifiche?"
        content={
          <span className="confirm-dialog-content">
            <span>Associazioni esistenti: {associationsBeforeUpload}</span>
            <span>
              Associazioni dopo il caricamento: {associationsAfterUpload}
            </span>
          </span>
        }
        buttonNoText={"ANNULLA"}
        buttonYesText={"CONFERMA"}
      />
      <InfoDialog
        show={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title={""}
        content={<span>Il file è stato caricato con successo.</span>}
        buttonText={"CHIUDI"}
      />
      {
        // @ts-ignore
        <MposLoader show={loading} />
      }
    </>
  );
};

export default UploadUsersFile;
